import { css } from '@adornis/chemistry/directives/css.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { BooleanAspect } from '@adornis/filter/aspect-schemas/BooleanAspect.js';
import { StringAspect } from '@adornis/filter/aspect-schemas/StringAspect.js';
import { ChromiumDialog } from '@chancenwerk/client/design/components/cr-dialog.js';
import { ChromiumSnackbar } from '@chancenwerk/client/design/components/cr-snackbar.js';
import { html, nothing } from 'lit';
import type { DateTime } from 'luxon';
import { firstValueFrom } from 'rxjs';
import { WorkTrackingAPI } from '../api/WorkTrackingAPI.js';
import { WorkTrackingAuslage } from '../entitites/WorkTrackingAuslage.js';
import {
  WorkTrackingSingleTaetigkeitenTypes,
  WorkTrackingTaetigkeit,
  WorkTrackingTaetigkeitenTypes,
} from '../entitites/WorkTrackingTaetigkeit.js';
import { WorkTrackingTaetigkeitenState } from '../entitites/WorkTrackingTaetigkeitenState.js';
import {
  DialogEditWorkTrackingAuslage,
  type WorkTrackingAuslageChange,
} from './chancen-dialog-edit-work-tracking-auslage.js';
import {
  DialogEditWorkTrackingTaetigkeit,
  type WorkTrackingTaetigkeitChange,
} from './chancen-dialog-edit-work-tracking-taetigkeit.js';
import { DialogWorkTrackingHistory } from './chancen-dialog-work-tracking-history.js';

export const finishedButton = (
  component: any,
  t: WorkTrackingTaetigkeit | WorkTrackingAuslage,
  currentUserID: string,
) => {
  return html` <cr-button
    mode="icon"
    icon="check"
    ${css({ color: component.colors.tone.success, scale: '1.4' })}
    @click=${async () => {
      t.changeState(currentUserID ?? '', WorkTrackingTaetigkeitenState.FINISHED);
      await t.save();
    }}
  >
  <cr-tooltip><cr-text>Als geklärt setzen<cr-text></cr-tooltip>
  </cr-button>`;
};

export const questionButton = (
  _component: any,
  _t: WorkTrackingTaetigkeit | WorkTrackingAuslage,
  _currentUserID: string,
) => {
  // ! removed for now
  return nothing;
};

export const editButton = (
  t: WorkTrackingTaetigkeit | WorkTrackingAuslage,
  currentUserID: string,
  fetchAgain: () => void,
) => {
  return html` <cr-button
    mode="icon"
    icon="edit"
    @click=${async () => {
      if (t instanceof WorkTrackingTaetigkeit) {
        const res = await DialogEditWorkTrackingTaetigkeit.showPopup<WorkTrackingTaetigkeitChange>({
          props: {
            overrideTitle: t.isSingle ? 'Einzeltätigkeit bearbeiten' : 'Tätigkeit bearbeiten',
            types: t.isSingle
              ? Object.values(WorkTrackingSingleTaetigkeitenTypes)
              : Object.values(WorkTrackingTaetigkeitenTypes),
            allowFreeType: t.isSingle,
            taetigkeit: new WorkTrackingTaetigkeit({ ...t.toJSON() }),
            extended: false,
          },
        });
        if (!res.taetigkeit) {
          return;
        }
        res.taetigkeit.addChanges(currentUserID ?? '', t, res.note);
        await res.taetigkeit.save();
      } else if (t instanceof WorkTrackingAuslage) {
        const res = await DialogEditWorkTrackingAuslage.showPopup<WorkTrackingAuslageChange>({
          props: {
            overrideTitle: 'Auslage bearbeiten',
            auslage: new WorkTrackingAuslage({ ...t.toJSON() }),
            extended: false,
          },
        });
        if (!res.auslage) {
          return;
        }
        res.auslage.addChanges(currentUserID ?? '', t, res.note);
        await res.auslage.save();
      }

      fetchAgain();
    }}
  >
    Bearbeiten
    <cr-tooltip><cr-text>Eintrag bearbeiten<cr-text></cr-tooltip>
  </cr-button>`;
};

export const disableButton = (
  component: any,
  t: WorkTrackingTaetigkeit,
  currentUserID: string,
  fetchAgain: () => void,
) => {
  return html` <cr-button
    mode="icon"
    icon=${t.disabled ? 'restore' : 'delete'}
    ${css({ color: t.disabled ? component.colors.tone.neutral : component.colors.tone.error, scale: '1.4' })}
    @click=${async () => {
      t.addChanges(currentUserID ?? '', new WorkTrackingTaetigkeit({ ...t.toJSON(), disabled: !t.disabled }), '');
      t.disabled = !t.disabled;
      await t.save();
      fetchAgain();
    }}
  >
  <cr-tooltip><cr-text>Als komplett streichen makieren<cr-text></cr-tooltip>
  </cr-button>`;
};

export const historyButton = (component: any, t: WorkTrackingTaetigkeit | WorkTrackingAuslage) => {
  return html` <cr-button
    mode="icon"
    icon="history"
    ${css({ color: component.colors.neutral, scale: '1.4' })}
    @click=${async () => {
      await DialogWorkTrackingHistory.showPopup({
        props: { changes: t.changes, nameFn: (val: string) => WorkTrackingTaetigkeit.fieldNames(val) },
      });
    }}
  >
    <cr-tooltip><cr-text>Historie<cr-text></cr-tooltip>
  </cr-button>`;
};

export const addSingleWorkAction = (
  userID: string | undefined,
  isSelf: () => boolean,
  outsideEntryDate: (date: DateTime) => boolean,
  fetchAgain: () => void,
) => {
  return async () => {
    const res = await DialogEditWorkTrackingTaetigkeit.showPopup<WorkTrackingTaetigkeitChange>({
      props: {
        overrideTitle: 'Einzeltätigkeit hinterlegen',
        types: Object.values(WorkTrackingSingleTaetigkeitenTypes),
        allowFreeType: true,
        userID: userID,
        extended: false,
      },
    });

    if (!res.taetigkeit || !res.taetigkeit.date) {
      return;
    }

    if (outsideEntryDate(res.taetigkeit.date)) {
      ChromiumSnackbar.show('Datum ist außerhalb des Monats!');
      return;
    }

    res.taetigkeit.isSingle = true;
    res.taetigkeit.generatedFromID = WorkTrackingTaetigkeit.NOT_GENERATED;
    if (isSelf()) {
      res.taetigkeit.stateContractor = WorkTrackingTaetigkeitenState.FINISHED;
    } else {
      res.taetigkeit.stateResponsible = WorkTrackingTaetigkeitenState.FINISHED;
    }
    await res.taetigkeit.create();
    fetchAgain();
  };
};

export const addWorkAction = (
  userID: string | undefined,
  isSelf: () => boolean,
  outsideEntryDate: (date: DateTime) => boolean,
  fetchAgain: () => void,
) => {
  return async () => {
    const res = await DialogEditWorkTrackingTaetigkeit.showPopup<WorkTrackingTaetigkeitChange>({
      props: {
        overrideTitle: 'Tätigkeit hinterlegen',
        types: Object.values(WorkTrackingTaetigkeitenTypes),
        userID: userID,
        extended: true,
      },
    });
    if (!res.taetigkeit || !res.taetigkeit.date) {
      return;
    }

    if (outsideEntryDate(res.taetigkeit.date)) {
      ChromiumSnackbar.show('Datum ist außerhalb des Monats!');
      return;
    }

    res.taetigkeit.createdByOwner = isSelf();
    if (isSelf()) {
      res.taetigkeit.stateContractor = WorkTrackingTaetigkeitenState.FINISHED;
    } else {
      res.taetigkeit.stateResponsible = WorkTrackingTaetigkeitenState.FINISHED;
    }
    await res.taetigkeit.create();
    fetchAgain();
  };
};

export const confirmAllAction = (userID: string | undefined, isSelf: () => boolean) => {
  return async () => {
    const res = await firstValueFrom(
      WorkTrackingAPI.subscribeTaetigkeiten(
        0,
        1000,
        new AdornisFilter({
          aspects: [
            new BooleanAspect({ fieldName: 'isSingle', value: true }),
            new StringAspect({ fieldName: 'userID', value: userID }),
          ],
        }),
      )({ stateContractor: 1, stateResponsible: 1 }),
    );

    for (const t of res) {
      if (isSelf()) {
        t.stateContractor = WorkTrackingTaetigkeitenState.FINISHED;
        await t.save();
      } else {
        t.stateResponsible = WorkTrackingTaetigkeitenState.FINISHED;
        await t.save();
      }
    }
  };
};

export const addAuslageAction = (
  userID: string | undefined,
  isSelf: () => boolean,
  outsideEntryDate: (date: DateTime) => boolean,
  fetchAgain: () => void,
) => {
  return async () => {
    const res = await DialogEditWorkTrackingAuslage.showPopup<WorkTrackingAuslageChange>({
      props: { overrideTitle: 'Auslage hinterlegen', userID: userID, extended: true },
    });
    if (!res.auslage || !res.auslage.date) {
      return;
    }

    if (outsideEntryDate(res.auslage.date)) {
      ChromiumSnackbar.show('Datum ist außerhalb des Monats!');
      return;
    }

    ChromiumDialog.showPopup({
      content: resolve => {
        return html`<cr-flex space="md" padding="lg">
          <cr-h3>Achtung!</cr-h3>
          <cr-text>Bitte denke dran das Original der Quittung einzuschicken!</cr-text>
          <cr-text>
            Chancenwerk e.V. Verwaltung <br />
            Am Markt 18 <br />
            44575 Castrop-Rauxel
          </cr-text>
          <cr-button @click=${resolve}>Ok</cr-button>
        </cr-flex>`;
      },
    });

    if (isSelf()) {
      res.auslage.stateContractor = WorkTrackingTaetigkeitenState.FINISHED;
    } else {
      res.auslage.stateResponsible = WorkTrackingTaetigkeitenState.FINISHED;
    }
    await res.auslage.create();
    fetchAgain();
  };
};
